import React, { useCallback, useEffect, useState } from 'react'
import { Modal, Spin } from 'antd'
import { Icon } from '@iconify/react'
import { useDispatch } from 'react-redux'

import ClientProfilePictureField, {
    EMPTY_CLIENT_PROFILE,
} from '../../modules/clients/components/ClientProfilePictureField'
import { updateClient } from '../../modules/clients/clients.slice'
import { setUserProfile } from '../../modules/login/login.slice'
import { findDefaultAvatarOptionById } from '../../assets/clients/avatar'
import { uploadDefaultAvatarAssetToS3 } from '../../modules/clients/utils/clientImageUpload'
import { resolveProfilePictureUrl } from '../../components/MentionEditor/MentionEditorMentionBlot'
import { showActionNotification } from '../../components/notification'

import modalStyles from '../../modules/clients/components/CreateClientModal.module.css'

const hasProfileSelection = (state) => {
    if (state?.mode === 'upload' && state?.uploadedUrl) return true
    if (state?.defaultAvatarId) return true
    return false
}

const SidebarProfilePhotoModal = ({ open, onClose, userProfile }) => {
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const [profileState, setProfileState] = useState(EMPTY_CLIENT_PROFILE)

    const initialProfilePicture = resolveProfilePictureUrl(
        userProfile?.profilePicture
    )

    useEffect(() => {
        if (!open) {
            setProfileState(EMPTY_CLIENT_PROFILE)
        }
    }, [open])

    const resolveProfilePictureForSubmit = useCallback(async () => {
        if (profileState.mode === 'upload') {
            if (profileState.uploadedUrl) return profileState.uploadedUrl
            return undefined
        }

        if (!profileState.defaultAvatarId && profileState.uploadedUrl) {
            return profileState.uploadedUrl
        }

        const option = findDefaultAvatarOptionById(profileState.defaultAvatarId)
        if (!option?.src) return undefined

        try {
            return await uploadDefaultAvatarAssetToS3(
                option.src,
                option.id,
                dispatch
            )
        } catch {
            return undefined
        }
    }, [profileState, dispatch])

    const handleSave = async () => {
        const userId = userProfile?._id
        if (!userId) return

        const shouldUpload =
            profileState.touched ||
            (!initialProfilePicture && hasProfileSelection(profileState))

        if (!shouldUpload) {
            onClose()
            return
        }

        setLoading(true)
        try {
            const profilePictureUrl = await resolveProfilePictureForSubmit()
            if (!profilePictureUrl) {
                showActionNotification({
                    type: 'error',
                    message: 'Could not prepare profile photo',
                    notificationKey: 'sidebar-profile-photo-failed',
                })
                return
            }

            if (profilePictureUrl === initialProfilePicture) {
                onClose()
                return
            }

            const result = await dispatch(
                updateClient({
                    id: userId,
                    data: { profilePicture: profilePictureUrl },
                })
            )

            const data = result?.payload?.data
            if (data?.success) {
                dispatch(
                    setUserProfile({
                        ...userProfile,
                        profilePicture: profilePictureUrl,
                    })
                )
                showActionNotification({
                    type: 'success',
                    message: 'Profile photo updated',
                    notificationKey: 'sidebar-profile-photo-updated',
                })
                onClose()
            } else {
                showActionNotification({
                    type: 'error',
                    message:
                        data?.message ||
                        'Failed to update profile photo. Please try again.',
                    notificationKey: 'sidebar-profile-photo-failed',
                })
            }
        } catch {
            showActionNotification({
                type: 'error',
                message: 'Failed to update profile photo. Please try again.',
                notificationKey: 'sidebar-profile-photo-failed',
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            title="Update profile photo"
            open={open}
            onCancel={loading ? undefined : onClose}
            footer={
                <div className={modalStyles.modalFooter}>
                    <div className={modalStyles.formActions}>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className={modalStyles.cancelButton}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={loading}
                            onClick={handleSave}
                            className={`${modalStyles.submitButton} ${
                                loading ? modalStyles.submitButtonDisabled : ''
                            }`}
                        >
                            {loading ? 'Saving...' : 'Save photo'}
                        </button>
                    </div>
                </div>
            }
            width={580}
            className="create-client-modal"
            destroyOnHidden
            centered
        >
            <Spin
                spinning={loading}
                indicator={
                    <Icon
                        icon="line-md:loading-loop"
                        style={{ fontSize: 34, color: '#6366f1' }}
                        aria-hidden
                    />
                }
            >
                <div className="create-client-form">
                    <ClientProfilePictureField
                        value={profileState}
                        onChange={setProfileState}
                        gender={userProfile?.gender}
                        disabled={loading}
                        dispatch={dispatch}
                        existingProfilePicture={userProfile?.profilePicture}
                    />
                </div>
            </Spin>
        </Modal>
    )
}

export default SidebarProfilePhotoModal
